import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers,
  FiUserPlus,
  FiUserCheck,
  FiUserX,
  FiArrowLeft,
  FiDownload,
  FiPlus,
  FiSearch,
  FiFileText,
  FiCalendar,
  FiActivity,
  FiHeart,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiPaperclip,
  FiTrash2,
  FiUpload,
  FiX,
  FiDollarSign,
  FiSave,
  FiSend,
} from "react-icons/fi";
import * as XLSX from "xlsx";
import Header from "../components/layout/Header";
import JuliaPanelAside from "../components/layout/JuliaPanelAside";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Avatar from "../components/ui/Avatar";
import Modal from "../components/ui/Modal";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import { Employee, BudgetRH } from "../types";
import { employeesApi } from "../api/employees";
import client from "../api/client";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import toast from "react-hot-toast";
import SubModuleCard from "../components/ui/SubModuleCard";

interface PieceJointe {
  nom: string;
  chemin: string;
  type: string;
  date_ajout: string;
}

// ── Registre unique du personnel ─────────────────────────────────────────────

const RegistreUnique: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [contractType, setContractType] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  const [uploadingPJ, setUploadingPJ] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    first_name: "",
    last_name: "",
    email: "",
    position: "",
    department: "",
    contract_type: "CDI" as const,
    hire_date: "",
    nationality: "",
    date_naissance: "",
    sexe: "",
    qualification: "",
  });

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeesApi.list({
        search,
        contract_type: contractType,
      });
      const d = res.data as any;
      setEmployees(d.employees || d.data || []);
    } catch {
      setEmployees([
        {
          id: "1",
          company_id: "1",
          first_name: "Marie",
          last_name: "Dupont",
          email: "marie@test.fr",
          position: "Directrice RH",
          department: "RH",
          contract_type: "CDI",
          hire_date: "2020-03-15",
          status: "active",
          created_at: "",
          updated_at: "",
          nationality: "Française",
          date_naissance: "1985-03-15",
          sexe: "F",
          qualification: "Cadre",
        },
        {
          id: "2",
          company_id: "1",
          first_name: "Thomas",
          last_name: "Martin",
          email: "thomas@test.fr",
          position: "Développeur Senior",
          department: "Technique",
          contract_type: "CDI",
          hire_date: "2021-06-01",
          status: "active",
          created_at: "",
          updated_at: "",
          nationality: "Française",
          date_naissance: "1990-07-22",
          sexe: "M",
          qualification: "Cadre",
        },
        {
          id: "3",
          company_id: "1",
          first_name: "Sophie",
          last_name: "Bernard",
          email: "sophie@test.fr",
          position: "Commerciale",
          department: "Commercial",
          contract_type: "CDD",
          hire_date: "2024-01-10",
          status: "active",
          created_at: "",
          updated_at: "",
          nationality: "Française",
          date_naissance: "1992-11-05",
          sexe: "F",
          qualification: "Employée",
        },
        {
          id: "4",
          company_id: "1",
          first_name: "Lucas",
          last_name: "Petit",
          email: "lucas@test.fr",
          position: "Stagiaire Marketing",
          department: "Marketing",
          contract_type: "Stage",
          hire_date: "2026-02-01",
          status: "active",
          created_at: "",
          updated_at: "",
          nationality: "Française",
          date_naissance: "2002-04-18",
          sexe: "M",
          qualification: "Stagiaire",
        },
        {
          id: "5",
          company_id: "1",
          first_name: "Emma",
          last_name: "Leroy",
          email: "emma@test.fr",
          position: "Comptable",
          department: "Finance",
          contract_type: "CDI",
          hire_date: "2019-09-01",
          status: "active",
          created_at: "",
          updated_at: "",
          nationality: "Française",
          date_naissance: "1988-12-30",
          sexe: "F",
          qualification: "Agent de maîtrise",
        },
      ] as any);
    } finally {
      setLoading(false);
    }
  }, [search, contractType]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleAdd = async () => {
    setAddLoading(true);
    try {
      await employeesApi.create({
        first_name: newEmployee.first_name,
        last_name: newEmployee.last_name,
        email: newEmployee.email,
        position: newEmployee.position,
        department: newEmployee.department,
        contract_type: newEmployee.contract_type,
        hire_date: newEmployee.hire_date,
        nationality: newEmployee.nationality,
        date_naissance: newEmployee.date_naissance,
        sexe: newEmployee.sexe,
        qualification: newEmployee.qualification,
      } as any);
      toast.success("Salarié ajouté avec succès");
      setShowAddModal(false);
      setNewEmployee({
        first_name: "",
        last_name: "",
        email: "",
        position: "",
        department: "",
        contract_type: "CDI",
        hire_date: "",
        nationality: "",
        date_naissance: "",
        sexe: "",
        qualification: "",
      });
      loadEmployees();
    } catch {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setAddLoading(false);
    }
  };

  const handleUploadPJ = async (file: File) => {
    if (!selectedEmployee) return;
    setUploadingPJ(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await client.post(
        `/employees/${selectedEmployee.id}/attachments`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      const updated = (res.data as any).employee;
      setEmployees((prev) =>
        prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)),
      );
      setSelectedEmployee({ ...selectedEmployee, ...updated });
      toast.success("Pièce jointe ajoutée");
    } catch {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingPJ(false);
    }
  };

  const handleDeletePJ = async (pjIndex: number) => {
    if (!selectedEmployee) return;
    try {
      const res = await client.delete(
        `/employees/${selectedEmployee.id}/attachments/${pjIndex}`,
      );
      const updated = (res.data as any).employee;
      setEmployees((prev) =>
        prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)),
      );
      setSelectedEmployee({ ...selectedEmployee, ...updated });
      toast.success("Pièce jointe supprimée");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const exportExcel = () => {
    const rows = employees.map((e) => ({
      Nom: e.last_name,
      Prénom: e.first_name,
      Nationalité: (e as any).nationality || "",
      "Date de naissance": (e as any).date_naissance || "",
      Sexe: (e as any).sexe || "",
      "Emploi / Qualification": (e as any).qualification || e.position || "",
      "Emploi / Poste": e.position || "",
      Département: e.department || "",
      "Type de contrat": e.contract_type || "",
      "Date d'entrée": e.hire_date
        ? format(new Date(e.hire_date), "d MMM yyyy", { locale: fr })
        : "",
      "Date de sortie": (e as any).end_date
        ? format(new Date((e as any).end_date), "d MMM yyyy", { locale: fr })
        : "",
      Statut: e.status === "active" ? "Actif" : "Inactif",
      Email: e.email || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Registre du personnel");
    XLSX.writeFile(
      wb,
      `Registre_unique_personnel_${format(new Date(), "yyyy-MM-dd")}.xlsx`,
    );
    toast.success("Export Excel téléchargé");
  };

  const exportPDF = () => {
    const content = `
  <html><head><title>Registre unique du personnel</title>
  <style>body{font-family:Arial;font-size:10px;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:3px 5px;text-align:left;}th{background:#f0f0f0;font-weight:bold;}</style>
  </head><body>
  <h2>Registre unique du personnel</h2>
  <p>Édité le ${format(new Date(), "d MMMM yyyy", { locale: fr })} : Document conforme aux articles L1221-13 et D1221-23 du Code du travail</p>
  <table>
  <tr><th>Nom</th><th>Prénom</th><th>Nationalité</th><th>Naissance</th><th>Sexe</th><th>Qualification</th><th>Emploi</th><th>Contrat</th><th>Entrée</th><th>Sortie</th></tr>
  ${employees
    .map(
      (e) => `<tr>
  <td>${e.last_name}</td><td>${e.first_name}</td>
  <td>${(e as any).nationality || ""}</td>
  <td>${(e as any).date_naissance || ""}</td>
  <td>${(e as any).sexe || ""}</td>
  <td>${(e as any).qualification || ""}</td>
  <td>${e.position || ""}</td><td>${e.contract_type || ""}</td>
  <td>${e.hire_date ? format(new Date(e.hire_date), "d MMM yyyy", { locale: fr }) : ""}</td>
  <td>${(e as any).end_date ? format(new Date((e as any).end_date), "d MMM yyyy", { locale: fr }) : ""}</td>
  </tr>`,
    )
    .join("")}
  </table>
  </body></html>
  `;
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(content);
      w.document.close();
      w.print();
    }
  };

  const filtered = employees.filter((e) => {
    const q = search.toLowerCase();
    const match =
      !q ||
      `${e.first_name} ${e.last_name} ${e.position} ${e.department}`
        .toLowerCase()
        .includes(q);
    const ct = !contractType || e.contract_type === contractType;
    return match && ct;
  });

  const pjList: PieceJointe[] = (selectedEmployee as any)?.pieces_jointes || [];

  return (
    <div style={{ display: "flex", gap: "1.5rem" }}>
      {/* Partie gauche : tableau */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Barre d'outils */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ position: "relative" }}>
              <FiSearch
                size={14}
                style={{
                  position: "absolute",
                  left: "0.75rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--color-text-muted)",
                }}
              />
              <input
                placeholder="Rechercher un salarié..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  padding: "0.5rem 0.75rem 0.5rem 2.25rem",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  fontSize: "var(--font-size-sm)",
                  outline: "none",
                  fontFamily: "var(--font-family)",
                  width: 240,
                  background: "var(--color-bg)",
                }}
              />
            </div>
            <Select
              options={[
                { value: "", label: "Tous les contrats" },
                { value: "CDI", label: "CDI" },
                { value: "CDD", label: "CDD" },
                { value: "Alternance", label: "Alternance" },
                { value: "Stage", label: "Stage" },
                { value: "Interim", label: "Intérim" },
              ]}
              value={contractType}
              onChange={(e) => setContractType(e.target.value)}
              style={{ marginBottom: 0, width: 180 }}
            />
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button
              variant="outline"
              icon={<FiDownload size={14} />}
              onClick={exportExcel}
            >
              Excel
            </Button>
            <Button
              variant="outline"
              icon={<FiDownload size={14} />}
              onClick={exportPDF}
            >
              PDF
            </Button>
            <Button
              icon={<FiPlus size={16} />}
              onClick={() => setShowAddModal(true)}
            >
              Ajouter un salarié
            </Button>
          </div>
        </div>

        {/* Info réglementaire */}
        <div
          style={{
            padding: "0.5rem 0.75rem",
            marginBottom: "1rem",
            borderRadius: "var(--radius-md)",
            background: "var(--color-primary)08",
            fontSize: "var(--font-size-xs)",
            color: "var(--color-text-secondary)",
          }}
        >
          Registre conforme aux articles L1221-13 et D1221-23 du Code du
          travail. Cliquez sur un salarié pour accéder à ses pièces jointes.
        </div>

        {/* Tableau avec colonnes réglementaires */}
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "var(--font-size-sm)",
            }}
          >
            <thead>
              <tr
                style={{ borderBottom: "2px solid var(--color-border-light)" }}
              >
                {[
                  "Salarié",
                  "Nationalité",
                  "Naissance",
                  "Sexe",
                  "Qualification",
                  "Contrat",
                  "Entrée",
                  "Sortie",
                  "Statut",
                  "PJ",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "0.625rem 0.5rem",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: "0.65rem",
                      color: "var(--color-text-secondary)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={10}
                    style={{
                      textAlign: "center",
                      padding: "3rem",
                      color: "var(--color-text-muted)",
                    }}
                  >
                    Chargement...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    style={{
                      textAlign: "center",
                      padding: "3rem",
                      color: "var(--color-text-muted)",
                    }}
                  >
                    Aucun salarié
                  </td>
                </tr>
              ) : (
                filtered.map((e, i) => {
                  const empPJ: PieceJointe[] = (e as any).pieces_jointes || [];
                  return (
                    <motion.tr
                      key={e.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => setSelectedEmployee(e)}
                      style={{
                        borderBottom: "1px solid var(--color-border-light)",
                        cursor: "pointer",
                        transition: "background 0.15s",
                        background:
                          selectedEmployee?.id === e.id
                            ? "var(--color-primary)08"
                            : "transparent",
                      }}
                      onMouseEnter={(ev) => {
                        if (selectedEmployee?.id !== e.id)
                          (ev.currentTarget as HTMLElement).style.background =
                            "var(--color-surface-hover)";
                      }}
                      onMouseLeave={(ev) => {
                        if (selectedEmployee?.id !== e.id)
                          (ev.currentTarget as HTMLElement).style.background =
                            "transparent";
                      }}
                    >
                      <td style={{ padding: "0.625rem 0.5rem" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <Avatar
                            name={`${e.first_name} ${e.last_name}`}
                            size={28}
                          />
                          <div>
                            <p
                              style={{
                                fontWeight: 500,
                                margin: 0,
                                fontSize: "var(--font-size-sm)",
                              }}
                            >
                              {e.last_name} {e.first_name}
                            </p>
                            <p
                              style={{
                                fontSize: "0.65rem",
                                color: "var(--color-text-muted)",
                                margin: 0,
                              }}
                            >
                              {e.position || "-"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "0.625rem 0.5rem",
                          fontSize: "var(--font-size-xs)",
                        }}
                      >
                        {(e as any).nationality || "-"}
                      </td>
                      <td
                        style={{
                          padding: "0.625rem 0.5rem",
                          fontSize: "var(--font-size-xs)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {(e as any).date_naissance
                          ? format(
                              new Date((e as any).date_naissance),
                              "dd/MM/yyyy",
                            )
                          : "-"}
                      </td>
                      <td
                        style={{
                          padding: "0.625rem 0.5rem",
                          fontSize: "var(--font-size-xs)",
                        }}
                      >
                        {(e as any).sexe || "-"}
                      </td>
                      <td
                        style={{
                          padding: "0.625rem 0.5rem",
                          fontSize: "var(--font-size-xs)",
                        }}
                      >
                        {(e as any).qualification || "-"}
                      </td>
                      <td style={{ padding: "0.625rem 0.5rem" }}>
                        <Badge variant="info">{e.contract_type}</Badge>
                      </td>
                      <td
                        style={{
                          padding: "0.625rem 0.5rem",
                          fontSize: "var(--font-size-xs)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {e.hire_date
                          ? format(new Date(e.hire_date), "dd/MM/yyyy")
                          : "-"}
                      </td>
                      <td
                        style={{
                          padding: "0.625rem 0.5rem",
                          fontSize: "var(--font-size-xs)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {(e as any).end_date
                          ? format(new Date((e as any).end_date), "dd/MM/yyyy")
                          : "-"}
                      </td>
                      <td style={{ padding: "0.625rem 0.5rem" }}>
                        <Badge
                          variant={e.status === "active" ? "success" : "danger"}
                        >
                          {e.status === "active" ? "Actif" : "Inactif"}
                        </Badge>
                      </td>
                      <td
                        style={{
                          padding: "0.625rem 0.5rem",
                          textAlign: "center",
                        }}
                      >
                        {empPJ.length > 0 ? (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.25rem",
                              color: "var(--color-primary)",
                              fontSize: "var(--font-size-xs)",
                            }}
                          >
                            <FiPaperclip size={12} /> {empPJ.length}
                          </span>
                        ) : (
                          <span
                            style={{
                              color: "var(--color-text-muted)",
                              fontSize: "var(--font-size-xs)",
                            }}
                          >
                            {" "}
                            -{" "}
                          </span>
                        )}
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Panneau droit : pièces jointes du salarié sélectionné */}
      <AnimatePresence>
        {selectedEmployee && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            style={{
              width: 320,
              flexShrink: 0,
              background: "var(--color-surface)",
              border: "1px solid var(--color-border-light)",
              borderRadius: "var(--radius-lg)",
              padding: "1.25rem",
              alignSelf: "flex-start",
              position: "sticky",
              top: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "1rem",
              }}
            >
              <h4
                style={{
                  margin: 0,
                  fontSize: "var(--font-size-sm)",
                  fontWeight: 600,
                }}
              >
                Dossier de {selectedEmployee.first_name}{" "}
                {selectedEmployee.last_name}
              </h4>
              <button
                onClick={() => setSelectedEmployee(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--color-text-muted)",
                  padding: "0.25rem",
                }}
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Lien vers fiche complète */}
            <button
              onClick={() => navigate(`/employes/${selectedEmployee.id}`)}
              style={{
                width: "100%",
                padding: "0.5rem",
                marginBottom: "1rem",
                background: "var(--color-primary)10",
                color: "var(--color-primary)",
                border: "none",
                borderRadius: "var(--radius-md)",
                cursor: "pointer",
                fontSize: "var(--font-size-xs)",
                fontWeight: 500,
              }}
            >
              Voir la fiche complète
            </button>

            {/* Upload PJ */}
            <div style={{ marginBottom: "1rem" }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUploadPJ(f);
                  e.target.value = "";
                }}
              />
              <Button
                variant="outline"
                icon={<FiUpload size={14} />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPJ}
                style={{ width: "100%", justifyContent: "center" }}
              >
                {uploadingPJ ? "Envoi en cours..." : "Ajouter une pièce jointe"}
              </Button>
            </div>

            {/* Liste des PJ */}
            {pjList.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "1.5rem 0",
                  color: "var(--color-text-muted)",
                }}
              >
                <FiPaperclip
                  size={24}
                  style={{ opacity: 0.3, marginBottom: "0.5rem" }}
                />
                <p style={{ fontSize: "var(--font-size-xs)", margin: 0 }}>
                  Aucune pièce jointe
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                {pjList.map((pj, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.5rem",
                      borderRadius: "var(--radius-md)",
                      background: "var(--color-bg)",
                      border: "1px solid var(--color-border-light)",
                    }}
                  >
                    <FiFileText
                      size={14}
                      style={{ color: "var(--color-primary)", flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          fontSize: "var(--font-size-xs)",
                          fontWeight: 500,
                          margin: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {pj.nom}
                      </p>
                      <p
                        style={{
                          fontSize: "0.6rem",
                          color: "var(--color-text-muted)",
                          margin: 0,
                        }}
                      >
                        {pj.type.toUpperCase()} :{" "}
                        {pj.date_ajout
                          ? format(new Date(pj.date_ajout), "dd/MM/yyyy")
                          : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeletePJ(idx)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--color-danger)",
                        padding: "0.25rem",
                        flexShrink: 0,
                      }}
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal ajout */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Ajouter un salarié"
        size="lg"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <p
            style={{
              fontSize: "var(--font-size-xs)",
              color: "var(--color-text-secondary)",
              margin: "0 0 0.5rem",
              fontStyle: "italic",
            }}
          >
            Les champs marqués * sont obligatoires. Les champs Nationalité, Date
            de naissance, Sexe et Qualification sont requis par le Code du
            travail (Art. D1221-23).
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <Input
              label="Nom *"
              value={newEmployee.last_name}
              onChange={(e) =>
                setNewEmployee((p) => ({ ...p, last_name: e.target.value }))
              }
            />
            <Input
              label="Prénom *"
              value={newEmployee.first_name}
              onChange={(e) =>
                setNewEmployee((p) => ({ ...p, first_name: e.target.value }))
              }
            />
            <Input
              label="Nationalité"
              value={newEmployee.nationality}
              onChange={(e) =>
                setNewEmployee((p) => ({ ...p, nationality: e.target.value }))
              }
              placeholder="ex : Française"
            />
            <Input
              label="Date de naissance"
              value={newEmployee.date_naissance}
              onChange={(e) =>
                setNewEmployee((p) => ({
                  ...p,
                  date_naissance: e.target.value,
                }))
              }
              type="date"
            />
            <Select
              label="Sexe"
              options={[
                { value: "", label: "Sélectionner" },
                { value: "M", label: "Masculin" },
                { value: "F", label: "Féminin" },
              ]}
              value={newEmployee.sexe}
              onChange={(e) =>
                setNewEmployee((p) => ({ ...p, sexe: e.target.value }))
              }
            />
            <Input
              label="Qualification"
              value={newEmployee.qualification}
              onChange={(e) =>
                setNewEmployee((p) => ({ ...p, qualification: e.target.value }))
              }
              placeholder="ex : Cadre, Employé, Ouvrier..."
            />
            <Input
              label="Email"
              value={newEmployee.email}
              onChange={(e) =>
                setNewEmployee((p) => ({ ...p, email: e.target.value }))
              }
              type="email"
            />
            <Input
              label="Poste / emploi *"
              value={newEmployee.position}
              onChange={(e) =>
                setNewEmployee((p) => ({ ...p, position: e.target.value }))
              }
            />
            <Input
              label="Département"
              value={newEmployee.department}
              onChange={(e) =>
                setNewEmployee((p) => ({ ...p, department: e.target.value }))
              }
            />
            <Select
              label="Type de contrat"
              options={[
                { value: "CDI", label: "CDI" },
                { value: "CDD", label: "CDD" },
                { value: "Alternance", label: "Alternance" },
                { value: "Stage", label: "Stage" },
                { value: "Interim", label: "Intérim" },
              ]}
              value={newEmployee.contract_type}
              onChange={(e) =>
                setNewEmployee((p) => ({
                  ...p,
                  contract_type: e.target.value as any,
                }))
              }
            />
            <Input
              label="Date d'entrée *"
              value={newEmployee.hire_date}
              onChange={(e) =>
                setNewEmployee((p) => ({ ...p, hire_date: e.target.value }))
              }
              type="date"
            />
          </div>
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
              marginTop: "0.5rem",
            }}
          >
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleAdd} disabled={addLoading}>
              {addLoading ? "Enregistrement..." : "Ajouter le salarié"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ── Parcours de recrutement ───────────────────────────────────────────────────

const Recrutement: React.FC = () => {
  const phases = [
    {
      num: 1,
      titre: "Identification du besoin",
      color: "var(--color-primary)",
      items: [
        "Date d'identification du besoin",
        "Construction de la fiche de poste",
        "Analyse JULIA : JULIA pose des questions pour comprendre et affiner le besoin",
      ],
    },
    {
      num: 2,
      titre: "Construction du calendrier",
      color: "var(--color-secondary)",
      items: [
        "Date de création de l'offre d'emploi",
        "Date de diffusion de l'offre",
        "Dates des entretiens + nombres + personnes présentes",
        "Date de sélection du profil",
        "Date de réponses finales aux candidats",
      ],
    },
    {
      num: 3,
      titre: "Outils clés en main",
      color: "#0284C7",
      items: [
        "Fiche de poste complétée et adaptée",
        "Offre d'emploi",
        "Guide d'entretien avec grille d'évaluation",
        "Mails de réponse aux candidats (refus / acceptation)",
        "Récapitulatif des jobboards pertinents",
      ],
    },
    {
      num: 4,
      titre: "Recrutement réalisé",
      color: "var(--color-warning)",
      items: [
        "Mail de promesse d'embauche",
        "Contrat de travail",
        "Notification DPAE + lien URSSAF",
        "Fiche de commande du matériel",
      ],
    },
    {
      num: 5,
      titre: "On-boarding",
      color: "var(--color-success)",
      items: [
        "Documents à remettre au salarié : livret d'accueil, organigramme",
        "Planning de la première semaine (EDT)",
        "Liste des goodies et petites attentions",
        "Guide d'entretien de suivi pour le N+1",
      ],
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div
        style={{
          padding: "0.75rem 1rem",
          background: "var(--color-primary)10",
          borderRadius: "var(--radius-md)",
          fontSize: "var(--font-size-sm)",
          color: "var(--color-primary)",
          marginBottom: "0.5rem",
        }}
      >
        JULIA vous accompagne pas à pas dans chaque recrutement. Cliquez sur
        "Démarrer un recrutement" pour lancer le processus guidé.
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "0.5rem",
        }}
      >
        <Button
          icon={<FiPlus size={16} />}
          onClick={() =>
            toast.success("Fonctionnalité disponible prochainement")
          }
        >
          Démarrer un recrutement
        </Button>
      </div>
      {phases.map((phase) => (
        <Card key={phase.num}>
          <div
            style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: phase.color + "20",
                color: phase.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "var(--font-size-base)",
                flexShrink: 0,
              }}
            >
              {phase.num}
            </div>
            <div style={{ flex: 1 }}>
              <h4
                style={{
                  margin: "0 0 0.5rem",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: 600,
                  color: phase.color,
                }}
              >
                Phase {phase.num} : {phase.titre}
              </h4>
              <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                {phase.items.map((item, i) => (
                  <li
                    key={i}
                    style={{
                      fontSize: "var(--font-size-sm)",
                      color: "var(--color-text-secondary)",
                      marginBottom: "0.25rem",
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

// ── Accompagner mes salariés ─────────────────────────────────────────────────

const AccompagnerSalaries: React.FC = () => {
  const sections = [
    {
      icon: <FiFileText size={20} />,
      titre: "Vie du contrat",
      color: "var(--color-primary)",
      items: [
        "Rédiger un contrat de travail (CDI, CDD, alternance, stage)",
        "Rédiger des avenants au contrat",
      ],
    },
    {
      icon: <FiCalendar size={20} />,
      titre: "Absentéisme",
      color: "var(--color-warning)",
      items: [
        "Suivi des absences maladie : saisie et analytique",
        "Suivi des congés : saisie + vue analytique",
        "Tous types d'absences : enfants malades, sans solde, grève...",
      ],
    },
    {
      icon: <FiActivity size={20} />,
      titre: "Parcours professionnel",
      color: "#0284C7",
      items: [
        "Entretien annuel : créer une trame, calendrier prévisionnel, analyse JULIA des contenus, suivi des propositions",
        "Plan de formation : JULIA analyse les besoins et propose un calendrier",
        "Mise en relation avec des prestataires de formation",
      ],
    },
    {
      icon: <FiHeart size={20} />,
      titre: "Santé et sécurité au travail",
      color: "var(--color-success)",
      items: [
        "Suivi des visites médicales + alertes",
        "Préconisations médicales et analyse des coûts",
        "Tableau de suivi des salariés RQTH",
      ],
    },
    {
      icon: <FiAlertCircle size={20} />,
      titre: "Disciplinaire & contentieux",
      color: "var(--color-danger)",
      items: [
        "Analyse de la situation par JULIA (sanction, licenciement, rappel à l'ordre)",
        "Proposition de calendrier et trames : convocation, courrier de licenciement",
        "Orientation vers un avocat partenaire si contentieux",
      ],
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {sections.map((s, i) => (
        <Card key={i}>
          <div
            style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "var(--radius-md)",
                background: s.color + "18",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span style={{ color: s.color }}>{s.icon}</span>
            </div>
            <div style={{ flex: 1 }}>
              <h4
                style={{
                  margin: "0 0 0.5rem",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: 600,
                }}
              >
                {s.titre}
              </h4>
              <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                {s.items.map((item, j) => (
                  <li
                    key={j}
                    style={{
                      fontSize: "var(--font-size-sm)",
                      color: "var(--color-text-secondary)",
                      marginBottom: "0.25rem",
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <Button
              variant="outline"
              onClick={() => toast.success("Module en cours de déploiement")}
            >
              Accéder
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

// ── Accompagner le départ ─────────────────────────────────────────────────────

const AccompagnerDepart: React.FC = () => {
  const types = [
    {
      titre: "Fin de CDD",
      color: "var(--color-warning)",
      icon: <FiClock size={20} />,
      description:
        "Rétroplanning de fin de CDD, calcul des indemnités, documents de fin de contrat.",
      etapes: [
        "Notification au salarié avant la fin du contrat",
        "Solde de tout compte",
        "Attestation Pôle Emploi",
        "Certificat de travail",
      ],
    },
    {
      titre: "Rupture conventionnelle",
      color: "var(--color-primary)",
      icon: <FiCheckCircle size={20} />,
      description: "Procédure de rupture conventionnelle homologuée (R.C.H.).",
      etapes: [
        "Convocation à l'entretien",
        "Signature de la convention",
        "Délai de rétractation (15 jours)",
        "Demande d'homologation DREETS",
        "Versement de l'indemnité",
      ],
    },
    {
      titre: "Démission",
      color: "#0284C7",
      icon: <FiUserX size={20} />,
      description: "Gestion de la démission et du préavis.",
      etapes: [
        "Réception et accusé de réception de la démission",
        "Calcul et suivi du préavis",
        "Documents de fin de contrat",
      ],
    },
    {
      titre: "Abandon de poste",
      color: "var(--color-danger)",
      icon: <FiAlertCircle size={20} />,
      description: "Procédure en cas d'abandon de poste.",
      etapes: [
        "Mise en demeure de reprise du travail",
        "Analyse JULIA de la situation",
        "Procédure disciplinaire si nécessaire",
      ],
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div
        style={{
          padding: "0.75rem 1rem",
          background: "var(--color-warning)10",
          borderRadius: "var(--radius-md)",
          fontSize: "var(--font-size-sm)",
          color: "var(--color-warning)",
          marginBottom: "0.5rem",
        }}
      >
        JULIA prépare un rétroplanning des étapes à suivre et évalue le coût de
        chaque type de départ.
      </div>
      {types.map((t, i) => (
        <Card key={i}>
          <div
            style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "var(--radius-md)",
                background: t.color + "18",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span style={{ color: t.color }}>{t.icon}</span>
            </div>
            <div style={{ flex: 1 }}>
              <h4
                style={{
                  margin: "0 0 0.25rem",
                  fontWeight: 600,
                  fontSize: "var(--font-size-sm)",
                }}
              >
                {t.titre}
              </h4>
              <p
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--color-text-secondary)",
                  margin: "0 0 0.5rem",
                }}
              >
                {t.description}
              </p>
              <div
                style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}
              >
                {t.etapes.map((e, j) => (
                  <span
                    key={j}
                    style={{
                      fontSize: "0.65rem",
                      padding: "0.125rem 0.5rem",
                      borderRadius: "var(--radius-full)",
                      background: t.color + "15",
                      color: t.color,
                      fontWeight: 500,
                    }}
                  >
                    {e}
                  </span>
                ))}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => toast.success("Module en cours de déploiement")}
            >
              Démarrer
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

// ── Page principale ───────────────────────────────────────────────────────────

// ── Sous-module Piloter la masse salariale ───────────────────────────────────

const MasseSalariale: React.FC = () => {
  const [budget, setBudget] = useState<BudgetRH>({
    annee: new Date().getFullYear(),
    total: 0,
    details: [],
  });

  useEffect(() => {
    client
      .get("/structure")
      .then((r) => {
        const d = (r.data as any).data;
        if (d?.budget_rh) setBudget(d.budget_rh);
      })
      .catch(() => {});
  }, []);

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.5rem 0.75rem",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    fontSize: "var(--font-size-sm)",
    fontFamily: "var(--font-family)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "var(--font-size-xs)",
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "0.375rem",
    display: "block",
  };

  const addPoste = () =>
    setBudget((b) => ({
      ...b,
      details: [...b.details, { poste: "", montant: 0 }],
    }));
  const updatePoste = (
    i: number,
    key: "poste" | "montant",
    val: string | number,
  ) =>
    setBudget((b) => ({
      ...b,
      details: b.details.map((d, idx) =>
        idx === i ? { ...d, [key]: val } : d,
      ),
    }));
  const removePoste = (i: number) =>
    setBudget((b) => ({
      ...b,
      details: b.details.filter((_, idx) => idx !== i),
    }));

  const totalCalcule = budget.details.reduce(
    (sum, d) => sum + (Number(d.montant) || 0),
    0,
  );

  const handleSave = async () => {
    try {
      await client.put("/structure/masse-salariale", { budget_rh: budget });
      toast.success("Budget RH enregistré");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <Card header={<span>Budget RH annuel</span>}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          <div>
            <label style={labelStyle}>Année</label>
            <input
              style={inputStyle}
              type="number"
              value={budget.annee}
              onChange={(e) =>
                setBudget((b) => ({
                  ...b,
                  annee: parseInt(e.target.value) || new Date().getFullYear(),
                }))
              }
            />
          </div>
          <div>
            <label style={labelStyle}>Budget total RH (€)</label>
            <input
              style={inputStyle}
              type="number"
              value={budget.total || ""}
              placeholder="Budget total annuel"
              onChange={(e) =>
                setBudget((b) => ({
                  ...b,
                  total: parseFloat(e.target.value) || 0,
                }))
              }
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "0.75rem",
          }}
        >
          <span style={{ fontSize: "var(--font-size-sm)", fontWeight: 600 }}>
            Répartition par poste budgétaire
          </span>
          <Button
            onClick={addPoste}
            icon={<FiPlus size={14} />}
            variant="outline"
          >
            Ajouter un poste
          </Button>
        </div>

        {budget.details.map((d, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr auto",
              gap: "0.75rem",
              marginBottom: "0.5rem",
              alignItems: "center",
            }}
          >
            <input
              style={inputStyle}
              placeholder="ex : Salaires bruts, Charges patronales..."
              value={d.poste}
              onChange={(e) => updatePoste(i, "poste", e.target.value)}
            />
            <input
              style={inputStyle}
              type="number"
              placeholder="Montant €"
              value={d.montant || ""}
              onChange={(e) =>
                updatePoste(i, "montant", parseFloat(e.target.value) || 0)
              }
            />
            <button
              onClick={() => removePoste(i)}
              style={{
                color: "var(--color-danger)",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        ))}

        {budget.details.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0.75rem",
              background: "var(--color-bg)",
              borderRadius: "var(--radius-md)",
              marginTop: "1rem",
              fontWeight: 600,
              fontSize: "var(--font-size-sm)",
            }}
          >
            <span>Total calculé</span>
            <span
              style={{
                color:
                  totalCalcule > budget.total && budget.total > 0
                    ? "var(--color-danger)"
                    : "var(--color-success)",
              }}
            >
              {totalCalcule.toLocaleString("fr-FR")} €{" "}
              {budget.total > 0 &&
                `/ ${budget.total.toLocaleString("fr-FR")} €`}
            </span>
          </div>
        )}
      </Card>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button icon={<FiSave size={16} />} onClick={handleSave}>
          Enregistrer le budget
        </Button>
      </div>
    </div>
  );
};

// ── Sous-module Gestion des temps ──────────────────────────────────────────────

const GestionDesTemps: React.FC = () => {
  const [frequence, setFrequence] = useState<"hebdomadaire" | "mensuelle">(
    "hebdomadaire",
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <div
        style={{
          padding: "0.75rem 1rem",
          background: "var(--color-primary)10",
          borderRadius: "var(--radius-md)",
          fontSize: "var(--font-size-sm)",
          color: "var(--color-primary)",
        }}
      >
        Suivez les temps de travail de vos collaborateurs via des fiches
        auto-déclaratives. Les données collectées permettent de faire le lien
        avec votre logiciel de paie.
      </div>

      <Card header={<span>Configuration</span>}>
        <div style={{ marginBottom: "1rem" }}>
          <label
            style={{
              fontSize: "var(--font-size-xs)",
              fontWeight: 600,
              color: "var(--color-text-secondary)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "0.375rem",
              display: "block",
            }}
          >
            Fréquence d'envoi des fiches
          </label>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            {(["hebdomadaire", "mensuelle"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFrequence(f)}
                style={{
                  padding: "0.5rem 1.25rem",
                  borderRadius: "var(--radius-md)",
                  border: `1px solid ${frequence === f ? "var(--color-primary)" : "var(--color-border)"}`,
                  background:
                    frequence === f
                      ? "var(--color-primary)"
                      : "var(--color-surface)",
                  color:
                    frequence === f ? "#fff" : "var(--color-text-secondary)",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all var(--transition-fast)",
                  fontFamily: "var(--font-family)",
                }}
              >
                {f === "hebdomadaire" ? "Hebdomadaire" : "Mensuelle"}
              </button>
            ))}
          </div>
        </div>
        <p
          style={{
            fontSize: "var(--font-size-sm)",
            color: "var(--color-text-secondary)",
          }}
        >
          Les fiches de suivi seront envoyées chaque{" "}
          {frequence === "hebdomadaire" ? "lundi matin" : "1er du mois"} aux
          salariés concernés.
        </p>
      </Card>

      <Card header={<span>Fiches de suivi en cours</span>}>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "var(--color-text-muted)",
          }}
        >
          <FiClock
            size={32}
            style={{ marginBottom: "0.75rem", opacity: 0.5 }}
          />
          <p style={{ fontSize: "var(--font-size-sm)", marginBottom: "1rem" }}>
            Aucune fiche de suivi envoyée pour le moment.
          </p>
          <Button
            icon={<FiSend size={14} />}
            onClick={() => toast.success("Envoi des fiches de suivi programmé")}
          >
            Envoyer les fiches de suivi
          </Button>
        </div>
      </Card>

      <Card header={<span>Export vers la paie</span>}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "var(--radius-md)",
              background: "var(--color-success)15",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FiDownload size={18} style={{ color: "var(--color-success)" }} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 600, fontSize: "var(--font-size-sm)" }}>
              Exporter les heures déclarées
            </p>
            <p
              style={{
                fontSize: "var(--font-size-xs)",
                color: "var(--color-text-secondary)",
              }}
            >
              Générez un fichier compatible avec votre logiciel de paie
            </p>
          </div>
          <Button
            variant="outline"
            icon={<FiDownload size={14} />}
            onClick={() => toast.success("Export en cours de préparation")}
          >
            Exporter
          </Button>
        </div>
      </Card>
    </div>
  );
};

type SubModule =
  | "registre"
  | "recrutement"
  | "accompagnement"
  | "depart"
  | "masse-salariale"
  | "gestion-temps"
  | null;

const GererSalariesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeModule, setActiveModule] = useState<SubModule>(null);

  useEffect(() => {
    if (location.pathname !== "/employes") return;
    const hashToModule: Record<string, SubModule> = {
      "#annuaire": "registre",
      "#effectifs": "registre",
      "#dossiers": "registre",
      "#recrutement": "recrutement",
      "#recruter": "recrutement",
      "#integration": "recrutement",
      "#medical": "accompagnement",
      "#formation": "accompagnement",
      "#entretiens": "accompagnement",
      "#disciplinaire": "accompagnement",
      "#depart": "depart",
      "#masse-salariale": "masse-salariale",
      "#gestion-temps": "gestion-temps",
    };
    const module = hashToModule[location.hash] ?? null;
    setActiveModule(module);
  }, [location.pathname, location.hash]);

  const hashByModule: Record<Exclude<SubModule, null>, string> = {
    registre: "#effectifs",
    recrutement: "#recrutement",
    accompagnement: "#entretiens",
    depart: "#depart",
    "masse-salariale": "#masse-salariale",
    "gestion-temps": "#gestion-temps",
  };

  const openModule = (id: Exclude<SubModule, null>) => {
    setActiveModule(id);
    navigate({ pathname: "/employes", hash: hashByModule[id] });
  };

  const openHub = () => {
    setActiveModule(null);
    navigate({ pathname: "/employes", hash: "" });
  };

  const subModules = [
    {
      id: "registre" as SubModule,
      icon: <FiUsers size={24} />,
      title: "Registre unique du personnel",
      description:
        "État du personnel actuel et passé : document obligatoire dès le 1er salarié. Export Excel et PDF.",
      color: "var(--color-primary)",
    },
    {
      id: "recrutement" as SubModule,
      icon: <FiUserPlus size={24} />,
      title: "Recruter un nouveau salarié",
      description:
        "Parcours de recrutement guidé en 5 phases : identification du besoin, calendrier, outils, contrat, on-boarding",
      color: "var(--color-secondary)",
    },
    {
      id: "accompagnement" as SubModule,
      icon: <FiUserCheck size={24} />,
      title: "Accompagner mes salariés",
      description:
        "Vie du contrat, absentéisme, entretiens annuels, plan de formation, santé/sécurité, disciplinaire",
      color: "#0284C7",
    },
    {
      id: "depart" as SubModule,
      icon: <FiUserX size={24} />,
      title: "Accompagner le départ",
      description:
        "Rétroplanning et outils pour les fins de CDD, ruptures conventionnelles, démissions et abandons de poste",
      color: "var(--color-danger)",
    },
    {
      id: "masse-salariale" as SubModule,
      icon: <FiDollarSign size={24} />,
      title: "Piloter ma masse salariale",
      description: "Créer et piloter le budget RH pour l'année à venir",
      color: "var(--color-success)",
    },
    {
      id: "gestion-temps" as SubModule,
      icon: <FiClock size={24} />,
      title: "Gestion des temps",
      description:
        "Suivi des temps de travail par autodéclaration : lien avec votre logiciel de paie",
      color: "var(--color-accent)",
    },
  ];

  const titles: Record<string, string> = {
    registre: "Registre unique du personnel",
    recrutement: "Recruter un nouveau salarié",
    accompagnement: "Accompagner mes salariés",
    depart: "Accompagner le départ",
    "masse-salariale": "Piloter ma masse salariale",
    "gestion-temps": "Gestion des temps",
  };

  const renderContent = () => {
    switch (activeModule) {
      case "registre":
        return <RegistreUnique />;
      case "recrutement":
        return <Recrutement />;
      case "accompagnement":
        return <AccompagnerSalaries />;
      case "depart":
        return <AccompagnerDepart />;
      case "masse-salariale":
        return <MasseSalariale />;
      case "gestion-temps":
        return <GestionDesTemps />;
      default:
        return null;
    }
  };

  return (
    <div>
      <Header
        title={
          activeModule
            ? `Gérer mes salariés : ${titles[activeModule]}`
            : "Gérer mes salariés"
        }
        subtitle={
          activeModule
            ? undefined
            : "Registre, recrutement, suivi et accompagnement de vos collaborateurs"
        }
      />

      <div style={{ padding: "1.5rem 2rem" }}>
        {!activeModule ? (
          <div
            style={{
              display: "flex",
              gap: "2rem",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                flex: "1 1 320px",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                maxWidth: 860,
              }}
            >
              {subModules.map((mod) => (
                <SubModuleCard
                  key={mod.id}
                  icon={mod.icon}
                  title={mod.title}
                  description={mod.description}
                  color={mod.color}
                  onClick={() => openModule(mod.id as Exclude<SubModule, null>)}
                />
              ))}
            </div>
            <JuliaPanelAside
              moduleName="Gérer mes salariés"
              contextMessage="Je peux vous aider à gérer vos recrutements, suivre vos effectifs ou préparer un départ. Vous pouvez aussi me demander de vérifier la conformité de votre registre du personnel."
              pendingTasks={3}
            />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeModule}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div style={{ marginBottom: "1.5rem" }}>
                <button
                  onClick={openHub}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "var(--font-size-sm)",
                    color: "var(--color-text-secondary)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <FiArrowLeft size={16} /> Retour aux sous-modules
                </button>
              </div>
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default GererSalariesPage;
